import React from "react";
import { Modal, Button, Container, Row, Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { RxCross2 } from "react-icons/rx";
import { Formik } from "formik";
import * as Yup from "yup";
import api from "../../../api/axios";
import { companyID } from "../../../auth";

const LeaveConfigModal = ({
  visible,
  onClose,
  onSubmit,
  editingRecord,
  leaveTypesMaster = [],
  currentLeaveTypeName,
}) => {
  const { t } = useTranslation();

  const initialValues = {
    leave_id: editingRecord?.leave_id || "",
    total_days: editingRecord?.total_days || "",
    carry_forward: editingRecord?.carry_forward || false,
    leave_forwarded: editingRecord?.leave_forwarded || "",
    monthly_allow_leaves: editingRecord?.monthly_allow_leaves || "",
  };

  const validationSchema = Yup.object({
    leave_id: Yup.string().required(t("Please select a leave type")),
    total_days: Yup.number()
      .required(t("Please enter total days"))
      .min(0, t("Total days cannot be negative"))
      .max(365, t("Total days cannot exceed 365"))
      .integer(t("Total days must be a whole number"))
      .test(
        "min-total-days",
        t("Total days must be at least 1"),
        function (value) {
          const { leave_id } = this.parent;
          // Allow 0 for unpaid leave types, require at least 1 for others
          const leaveType = leaveTypesMaster.find(lt => lt.id == leave_id);
          if (leaveType && leaveType.check_leave_balance === 0) {
            return value >= 0; // Allow 0 for unpaid leave
          }
          return value >= 1; // Require at least 1 for other leave types
        }
      ),
    leave_forwarded: Yup.number()
      .when("carry_forward", {
        is: true,
        then: (schema) =>
          schema
            .required(t("Please enter leave forwarded days"))
            .min(1, t("Leave forwarded must be at least 1"))
            .test(
              "max-total-days",
              t("Leave forwarded cannot exceed total days"),
              function (value) {
                const { total_days } = this.parent;
                return !value || !total_days || value <= total_days;
              }
            ),
        otherwise: (schema) => schema.nullable(),
      })
      .integer(t("Leave forwarded must be a whole number")),
    monthly_allow_leaves: Yup.number()
      .min(0, t("Monthly allow leaves cannot be negative"))
      .max(31, t("Monthly allow leaves cannot exceed 31"))
      .integer(t("Monthly allow leaves must be a whole number"))
      .test(
        "max-annual-days",
        t("Monthly leaves cannot exceed total annual days"),
        function (value) {
          const { total_days } = this.parent;
          if (!value || !total_days) return true; // Allow empty or when total_days is not set
          return value <= total_days;
        }
      ),
  });

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const company_id = companyID();
      if (!company_id) {
        console.error("Company ID not found.");
        setSubmitting(false);
        return;
      }

      const payload = {
        company_id: parseInt(company_id),
        leave_id: parseInt(values.leave_id),
        total_days: parseInt(values.total_days),
        carry_forward: values.carry_forward ? 1 : 0,
        leave_forwarded: values.carry_forward
          ? parseInt(values.leave_forwarded)
          : null,
        monthly_allow_leaves: values.monthly_allow_leaves
          ? parseInt(values.monthly_allow_leaves)
          : 0,
      };

      let response;
      if (editingRecord) {
        response = await api.put(`/leave-config/${editingRecord.id}`, payload);
      } else {
        response = await api.post("/leave-config", payload);
      }

      if (response.data.success) {
        console.log(
          `Leave configuration ${
            editingRecord ? "updated" : "created"
          } successfully!`
        );
        onSubmit(values);
        onClose();
      } else {
        console.error(
          `Failed to ${editingRecord ? "update" : "create"} leave configuration:`,
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error saving leave configuration:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Create dropdown options that include current leave type when editing
  const getDropdownOptions = () => {
    if (editingRecord) {
      // When editing, show only the current leave type
      return [
        {
          id: editingRecord.leave_id,
          name: currentLeaveTypeName || editingRecord.name || "Current Leave Type"
        }
      ];
    }
    // When adding new, show available leave types
    return leaveTypesMaster;
  };

  // Check if selected leave type is unpaid leave
  const isUnpaidLeave = (leaveId) => {
    const leaveType = leaveTypesMaster.find(lt => lt.id == leaveId);
    return leaveType && leaveType.check_leave_balance === 0;
  };

  return (
    <Modal show={visible} size="lg" centered backdrop="static">
      <Modal.Header>
        <Modal.Title>
          {editingRecord ? t("Edit Leave Configuration") : t("Add Leave Configuration")}
        </Modal.Title>
        <button type="button" className="btn-close" onClick={onClose}>
          <span>
            <RxCross2 className="mb-2" />
          </span>
        </button>
      </Modal.Header>

      <Modal.Body>
        <Container>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              isValid,
              setFieldValue,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("Leave Type")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="leave_id"
                        value={values.leave_id}
                        onChange={(e) => {
                          handleChange(e);
                          // If unpaid leave is selected, set total_days to 0
                          if (isUnpaidLeave(e.target.value)) {
                            setFieldValue("total_days", 0);
                            setFieldValue("carry_forward", false);
                            setFieldValue("leave_forwarded", "");
                          }
                        }}
                        onBlur={handleBlur}
                        isInvalid={touched.leave_id && !!errors.leave_id}
                        disabled={!!editingRecord}
                      >
                        <option value="">{t("Select Leave Type")}</option>
                        {getDropdownOptions().map((leaveType) => (
                          <option key={leaveType.id} value={leaveType.id}>
                            {leaveType.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.leave_id}
                      </Form.Control.Feedback>
                      {editingRecord && (
                        <Form.Text className="text-muted">
                          {t("Leave type cannot be changed when editing")}
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("Total Days")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="total_days"
                        value={values.total_days}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("Enter total days")}
                        isInvalid={touched.total_days && !!errors.total_days}
                        min="0"
                        max="365"
                        disabled={isUnpaidLeave(values.leave_id)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.total_days}
                      </Form.Control.Feedback>
                      {isUnpaidLeave(values.leave_id) && (
                        <Form.Text className="text-muted">
                          {t("Unpaid leave has no balance limit")}
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{t("Monthly Allow Leaves")}</Form.Label>
                      <Form.Control
                        type="number"
                        name="monthly_allow_leaves"
                        value={values.monthly_allow_leaves}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("Enter monthly allow leaves")}
                        isInvalid={
                          touched.monthly_allow_leaves &&
                          !!errors.monthly_allow_leaves
                        }
                        min="0"
                        max="31"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.monthly_allow_leaves}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {t("Number of leaves allowed per month (0 for no monthly limit)")}
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="carry_forward"
                        label={t("Allow Carry Forward")}
                        checked={values.carry_forward}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFieldValue("carry_forward", isChecked);
                          if (!isChecked) {
                            setFieldValue("leave_forwarded", "");
                          }
                        }}
                        className="mb-3"
                        disabled={isUnpaidLeave(values.leave_id)}
                      />
                      {isUnpaidLeave(values.leave_id) && (
                        <Form.Text className="text-muted">
                          {t("Carry forward is not applicable for unpaid leave")}
                        </Form.Text>
                      )}
                    </Form.Group>

                    {values.carry_forward && (
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {t("Leave Forwarded Days")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="leave_forwarded"
                          value={values.leave_forwarded}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={t("Enter leave forwarded days")}
                          isInvalid={
                            touched.leave_forwarded && !!errors.leave_forwarded
                          }
                          min="1"
                          max={values.total_days || 365}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.leave_forwarded}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          {t("Maximum days that can be carried forward to next year")}
                        </Form.Text>
                      </Form.Group>
                    )}

                    <div className="alert alert-info">
                      <h6 className="alert-heading">{t("Configuration Summary")}</h6>
                      <hr />
                      <div className="mb-2">
                        <strong>{t("Leave Type")}:</strong>{" "}
                        {values.leave_id
                          ? (editingRecord 
                              ? currentLeaveTypeName 
                              : leaveTypesMaster.find((lt) => lt.id == values.leave_id)?.name) ||
                            t("Selected")
                          : t("Not selected")}
                      </div>
                      <div className="mb-2">
                        <strong>{t("Total Annual Days")}:</strong>{" "}
                        {values.total_days || 0}
                      </div>
                      <div className="mb-2">
                        <strong>{t("Monthly Limit")}:</strong>{" "}
                        {values.monthly_allow_leaves || t("No limit")}
                      </div>
                      <div className="mb-2">
                        <strong>{t("Carry Forward")}:</strong>{" "}
                        {values.carry_forward ? t("Yes") : t("No")}
                      </div>
                      {values.carry_forward && (
                        <div>
                          <strong>{t("Max Carry Forward")}:</strong>{" "}
                          {values.leave_forwarded || 0} {t("days")}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Modal.Footer>
                  <Button variant="secondary" onClick={onClose}>
                    {t("Cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  >
                    {editingRecord ? t("Update Configuration") : t("Save Configuration")}
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default LeaveConfigModal;
